import { useCallback, useEffect, useRef, useState } from 'react';

import { Message, Modal } from '@iwoca/orion';
import { RootState } from '@iwProvider/configureStore';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import { CheckoutLinkDebitCardForm } from './CheckoutLinkDebitCardForm';
import { CHECKOUT_DEBIT_CARD_REGISTERED } from '../../modals/addPaymentMethodModal/AddPaymentMethodModal.actions';
import { PaymentMethodAuthenticationModal } from '../../modals/paymentMethodAuthenticationModal/PaymentMethodAuthenticationModal';
import styles from '../LinkDebitCard.module.css';

const mapStateToProps = (state: RootState) => ({
    paymentMethodCount: state.api?.payment_method?.data?.length || 0
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    // Custom dispatch to hook into existing flows that Stripe uses - this closes the modal and updates data
    dispatchCardRegistered: (paymentMethodCountBefore: number) =>
        dispatch({
            type: CHECKOUT_DEBIT_CARD_REGISTERED,
            paymentMethodCountBefore
        })
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

const CheckoutLinkDebitCardBase = ({
    paymentMethodCount,
    dispatchCardRegistered,
    useSectionGridLayout = true
}: {
    useSectionGridLayout?: boolean;
} & PropsFromRedux) => {
    const intl = useIntl();

    // This is a slightly dirty way of hooking into existing sagas used by Stripe to ensure we update state correctly
    // When Checkout links the card, it dispatches a custom event which waits for a new payment method to appear in the API
    // With Stripe, it fetches current payment_method count before linking the card; however Checkout did not have such state, meaning the modal close/data refresh would hang around or refresh out of sync
    // This way, we can get the current count when the component is rendered and pass it through to the saga
    // NB the reason why we can't hardcode this is that bank_details also count as a payment_method, and adding new cards via profile also means we can have existing cards available.
    const paymentMethodCountRef = useRef(paymentMethodCount);
    const onCardRegistered = useCallback(
        () => dispatchCardRegistered(paymentMethodCountRef.current),
        [dispatchCardRegistered]
    );

    const [is3DSModalOpen, setIs3DSModalOpen] = useState(false);
    const [checkout3DSecureRedirectUrl, setCheckout3DSecureRedirectUrl] =
        useState<string | null>(null);
    const [authenticationFailed, setAuthenticationFailed] = useState(false);
    const [cardRegistered, setCardRegistered] = useState(false);

    useEffect(() => {
        const handle3DSCompleteEvent = (event: MessageEvent) => {
            if (event.data === '3DS-authentication-complete') {
                setIs3DSModalOpen(false);
                setCardRegistered(true);
                onCardRegistered();
            } else if (event.data === '3DS-authentication-failed') {
                setIs3DSModalOpen(false);
                setAuthenticationFailed(true);
                setCardRegistered(false);
            }
        };

        window.addEventListener('message', handle3DSCompleteEvent);
        return () =>
            window.removeEventListener('message', handle3DSCompleteEvent);
    }, [onCardRegistered]);

    return (
        <>
            {checkout3DSecureRedirectUrl && (
                <Modal
                    isOpen={is3DSModalOpen}
                    onClose={() => setIs3DSModalOpen(false)}
                >
                    <PaymentMethodAuthenticationModal
                        authenticationIframeURL={checkout3DSecureRedirectUrl}
                    />
                </Modal>
            )}
            {authenticationFailed && (
                <Message
                    className="mb-m"
                    message={intl.formatMessage({
                        id: '_.link_debit_card.error',
                        defaultMessage:
                            'Failed to register the card. Please try again.'
                    })}
                    type="error"
                />
            )}
            <div className={styles.LinkDebitCard}>
                <CheckoutLinkDebitCardForm
                    useSectionGridLayout={useSectionGridLayout}
                    setSubmitAsLoading={cardRegistered || is3DSModalOpen}
                    onSuccess={onCardRegistered}
                    onError={() => setAuthenticationFailed(true)}
                    onRetry={() => setAuthenticationFailed(false)}
                    on3DSRequired={(redirectUrl) => {
                        setCheckout3DSecureRedirectUrl(redirectUrl);
                        setIs3DSModalOpen(true);
                    }}
                />
            </div>
        </>
    );
};

export const CheckoutLinkDebitCard = connect(
    mapStateToProps,
    mapDispatchToProps
)(CheckoutLinkDebitCardBase);
